/**
 * CK | Progress Bar — ck-gt
 * Reads config from .ck-gt-pb-config elements (rendered by sections/ck-gt-progress-bar.liquid).
 * Multiple sections can coexist for per-market setups — the script picks the first one
 * where enabled === true. Creates its own #ck-gt-pb-mount at the top of the cart
 * items list (so the cart drawer carries no markup when the bar is unused) and
 * manages free-gift add/remove logic on cart changes.
 *
 * Free gifts: this bar adds and removes the gift line; the CK Custom Discount function
 * prices it to €0 off the _ck_gift / _ck_gift_auto tag.
 *
 * Each step is measured the way Shopify checks it, always excluding gift lines:
 *  - free shipping: total after discounts (Shopify's shipping rates do the same)
 *  - discount tier: subtotal before discounts of the discount's collection (Shopify's
 *    minimum purchase requirement); a discount can't count towards its own minimum
 *  - gifts: the block's "Measured on" setting, either of the two above.
 *
 * Theme integration (Apotheke-Zaversky): this theme has no eventBus. It re-renders the cart
 * drawer section via the Section Rendering API and then dispatches a `cart:updated` document
 * event (see assets/cart.js); we just re-read the cart and repaint the bar.
 */
(function () {
  'use strict';

  if (window.__ckGtPbLoaded) return;
  window.__ckGtPbLoaded = true;

  let _config = null;
  let _lastCart = null;
  let _fetchTimer = null;
  let _observer = null;
  let _cartSig = null; // last seen cart fingerprint, to ignore duplicate events
  let _pickerBusy = false; // guards gift add/remove so cart re-renders don't re-enter
  let _busyTimer = null;   // watchdog that force-clears _pickerBusy if a request stalls

  const CHECK_SVG_DEFAULT =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#2ea24d"></circle><path d="M4.5 8.2 6.9 10.6 11.6 5.4" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  function checkContent(config) {
    if (config.check_icon_type === 'image' && config.check_icon_url) {
      return '<img src="' + config.check_icon_url + '" alt="" loading="lazy">';
    }
    return config.check_icon_svg || CHECK_SVG_DEFAULT;
  }

  // Fingerprint of the cart (variant:qty pairs + total) so we can ignore duplicate
  // cart:updated events that don't actually change anything. The total is in there
  // because adding or removing a discount code changes it without touching any line.
  function cartSig(cart) {
    return (cart.items || [])
      .map((i) => i.variant_id + ':' + i.quantity)
      .sort()
      .join('|') + '@' + cart.total_price;
  }

  // ─── Config ────────────────────────────────────────────────────────────────

  function getConfig() {
    if (_config) return _config;
    const els = document.querySelectorAll('.ck-gt-pb-config');
    for (const el of els) {
      try {
        const cfg = JSON.parse(el.textContent);
        if (cfg.enabled) {
          _config = cfg;
          return _config;
        }
      } catch (e) {
        console.error('[ck-gt-pb] Failed to parse config', e);
      }
    }
    return null;
  }

  // ─── Mount point ───────────────────────────────────────────────────────────
  // The cart drawer no longer ships a mount div — we create one only when the bar
  // is in use, at the top of the cart items list. Re-created after each re-render.

  function getMount() {
    let mount = document.getElementById('ck-gt-pb-mount');
    if (mount) return mount;
    const drawer = document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
    if (!drawer) return null;
    const host = drawer.querySelector('.drawer__scrollable.flex-col');
    if (!host) return null;
    mount = document.createElement('div');
    mount.id = 'ck-gt-pb-mount';
    mount.hidden = true;
    host.insertBefore(mount, host.firstChild);
    return mount;
  }

  // The gift picker lives at the END of the scrollable area — after the upsell,
  // just before the sticky footer — and scrolls with the cart content (its own
  // container, separate from the bar's mount at the top).
  function getPickerHost() {
    let host = document.getElementById('ck-gt-pb-pickers');
    if (host) return host;
    const drawer = document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
    if (!drawer) return null;
    const scroll = drawer.querySelector('.drawer__scrollable.flex-col');
    if (!scroll) return null;
    host = document.createElement('div');
    host.id = 'ck-gt-pb-pickers';
    host.className = 'ck-gt-pb-pickers';
    scroll.appendChild(host);
    return host;
  }

  // ─── CSS vars ─────────────────────────────────────────────────────────────

  function applyCSSVars(config) {
    const mount = document.getElementById('ck-gt-pb-mount');
    if (!mount) return;
    // Only override the native theme colors when the merchant actually picks one;
    // otherwise the CSS falls back to the native free-shipping-bar tokens.
    if (config.bar_gradient) mount.style.setProperty('--ck-gt-pb-fill', config.bar_gradient);
    if (config.bar_bg_color) mount.style.setProperty('--ck-gt-pb-bg', config.bar_bg_color);
    if (config.icon_border_color) mount.style.setProperty('--ck-gt-pb-border', config.icon_border_color);
    mount.style.setProperty('--ck-gt-pb-icon-size', config.icon_size + 'px');
  }

  // ─── Money formatting ──────────────────────────────────────────────────────

  function formatMoney(cents, format) {
    const matches = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const type = matches ? matches[1] : 'amount';
    let str;
    switch (type) {
      case 'amount_no_decimals':
        str = Math.round(cents / 100).toString();
        break;
      case 'amount_with_comma_separator':
        str = (cents / 100).toFixed(2).replace('.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        str = Math.round(cents / 100).toString();
        break;
      default:
        str = (cents / 100).toFixed(2);
    }
    return format.replace(/\{\{[^}]+\}\}/, str);
  }

  // ─── Bar HTML ──────────────────────────────────────────────────────────────

  function buildBarHTML(config) {
    const thresholds = [...config.thresholds]
      .filter((t) => t.threshold_cents > 0)
      .sort((a, b) => a.threshold_cents - b.threshold_cents);
    if (thresholds.length === 0) return '';
    const max = thresholds[thresholds.length - 1].threshold_cents;
    const showCheck = config.show_unlocked_check !== false;
    const check = showCheck ? `<span class="ck-gt-pb__check">${checkContent(config)}</span>` : '';
    const reserveEnd = !isRichtextEmpty(thresholds[thresholds.length - 1].icon_label);

    const icons = thresholds
      .map((t, i) => {
        const pct = (t.threshold_cents / max) * 100;

        let iconContent = '';
        let colorStyle = '';
        if (t.icon_type === 'svg_code' && t.icon_svg) {
          iconContent = t.icon_svg;
          // The SVG uses currentColor — set the container color so the merchant's
          // pick tints it (locked state; unlocked icons are inverted to white by CSS).
          if (t.icon_color) colorStyle = `;color:${t.icon_color}`;
        } else if (t.icon_url) {
          iconContent = `<img src="${t.icon_url}" alt="" loading="lazy">`;
        }

        const label = isRichtextEmpty(t.icon_label)
          ? ''
          : `<span class="ck-gt-pb__label">${t.icon_label}</span>`;

        return `<div class="ck-gt-pb__icon" id="ck-gt-pb-icon-${i}" style="left:${pct}%${colorStyle}" data-idx="${i}" aria-hidden="true">${label}<span class="ck-gt-pb__disc">${iconContent}</span>${check}</div>`;
      })
      .join('');

    return `
      <div class="ck-gt-pb${reserveEnd ? ' ck-gt-pb--reserve-end' : ''}" id="ck-gt-pb" aria-live="polite">
        <div class="ck-gt-pb__text-above" id="ck-gt-pb-above"></div>
        <div class="ck-gt-pb__bar-outer">
          <div class="ck-gt-pb__bar-track">
            <div class="ck-gt-pb__fill" id="ck-gt-pb-fill" style="width:0%"></div>
          </div>
          <div class="ck-gt-pb__icons">${icons}</div>
        </div>
        <div class="ck-gt-pb__text-below" id="ck-gt-pb-below"></div>
      </div>
    `;
  }

  function isRichtextEmpty(html) {
    return !html || html.replace(/<[^>]*>/g, '').trim() === '';
  }

  // ─── Inject bar into mount point ───────────────────────────────────────────

  function injectBar() {
    const config = getConfig();
    if (!config || !config.enabled) return;

    const validThresholds = config.thresholds.filter((t) => t.threshold_cents > 0);
    const hasPickers = config.pickers && config.pickers.length > 0;
    if (validThresholds.length === 0 && !hasPickers) return;

    // Bar at the top of the scrollable (its mount gets wiped on re-render).
    if (validThresholds.length) {
      const mount = getMount();
      if (mount) {
        mount.innerHTML = buildBarHTML(config);
        mount.hidden = false;
        applyCSSVars(config);
      }
    }

    // Picker carousel host at the END of the scrollable (after the upsell).
    if (hasPickers) getPickerHost();

    // Immediately restore last known state so text never flashes empty
    if (_lastCart) {
      updateUI(_lastCart.ckTotals, _lastCart.items, config);
    }
  }

  // ─── Re-inject the bar/picker whenever a cart re-render wipes our mount ──────

  function injectIfWiped() {
    const config = getConfig();
    if (!config || !config.enabled) return;
    const needsBar = config.thresholds.some((t) => t.threshold_cents > 0);
    const needsPickers = config.pickers && config.pickers.length > 0;
    const barGone = needsBar && !document.getElementById('ck-gt-pb-mount');
    const pickerGone = needsPickers && !document.getElementById('ck-gt-pb-pickers');
    if (barGone || pickerGone) injectBar();
  }

  function observeRerenders() {
    if (_observer) return;
    const target = document.getElementById('CartDrawer') || document.querySelector('cart-drawer');
    if (!target) return;
    _observer = new MutationObserver(injectIfWiped);
    _observer.observe(target, { childList: true, subtree: true });
  }

  // ─── Update bar UI + pickers ───────────────────────────────────────────────

  function updateUI(ckTotals, cartItems, config) {
    updateBar(ckTotals, cartItems, config);
    renderPickers(ckTotals, cartItems, config);
  }

  function updateBar(ckTotals, cartItems, config) {
    const mount = document.getElementById('ck-gt-pb-mount');
    if (!mount || !mount.querySelector('.ck-gt-pb')) return;

    // Hide bar on empty cart
    const bar = document.getElementById('ck-gt-pb');
    if (bar) bar.hidden = cartItems.length === 0;
    if (cartItems.length === 0) return;

    const thresholds = [...config.thresholds]
      .filter((t) => t.threshold_cents > 0)
      .sort((a, b) => a.threshold_cents - b.threshold_cents);
    if (thresholds.length === 0) return;
    const max = thresholds[thresholds.length - 1].threshold_cents;
    const ckValues = thresholds.map((t) => ckStepValue(t, ckTotals));

    // Steps can be measured on different totals, so one total can't place the fill. It
    // runs up to the first locked step and shows that step's own progress, so it never
    // passes a step that isn't unlocked.
    const ckFirstLocked = thresholds.findIndex((t, i) => ckValues[i] < t.threshold_cents);
    let ratio = 1;
    if (ckFirstLocked !== -1) {
      const ckFrom = ckFirstLocked > 0 ? thresholds[ckFirstLocked - 1].threshold_cents : 0;
      const ckTo = thresholds[ckFirstLocked].threshold_cents;
      ratio = Math.min(Math.max(ckValues[ckFirstLocked], ckFrom), ckTo) / max;
    }

    // Icons sit in a track shortened by --ck-gt-pb-inset, so the fill uses that
    // same width or it overtakes an icon that isn't unlocked yet.
    const fill = document.getElementById('ck-gt-pb-fill');
    if (fill) {
      fill.style.width =
        ratio >= 1 ? '100%' : `calc((100% - var(--ck-gt-pb-inset, 0px)) * ${ratio})`;
    }

    // Icon states and find highest completed / next incomplete
    let lastDoneIdx = -1;
    let nextTodoIdx = -1;

    thresholds.forEach((t, i) => {
      const icon = document.getElementById(`ck-gt-pb-icon-${i}`);
      if (!icon) return;
      if (ckValues[i] >= t.threshold_cents) {
        icon.classList.add('ck-gt-pb__icon--unlocked');
        lastDoneIdx = i;
      } else {
        icon.classList.remove('ck-gt-pb__icon--unlocked');
        if (nextTodoIdx === -1) nextTodoIdx = i;
      }
    });

    // Text above — from the highest completed threshold
    const aboveEl = document.getElementById('ck-gt-pb-above');
    if (aboveEl) {
      const aboveRaw = lastDoneIdx >= 0 ? (thresholds[lastDoneIdx].text_above || '') : '';
      aboveEl.innerHTML = aboveRaw;
      aboveEl.hidden = isRichtextEmpty(aboveRaw);
    }

    // Text below — from the next incomplete threshold; hidden when all done
    const belowEl = document.getElementById('ck-gt-pb-below');
    if (belowEl) {
      if (nextTodoIdx === -1) {
        belowEl.innerHTML = '';
        belowEl.hidden = true;
      } else {
        const next = thresholds[nextTodoIdx];
        const remaining = next.threshold_cents - ckValues[nextTodoIdx];
        const formatted = formatMoney(remaining, config.money_format);
        belowEl.innerHTML = (next.text_below || '').replace(/(?:<strong>)?\[remaining\](?:<\/strong>)?/g, `<strong>${formatted}</strong>`);
        belowEl.hidden = isRichtextEmpty(next.text_below);
      }
    }
  }

  // ─── Gift picker ───────────────────────────────────────────────────────────

  function escHtml(s) {
    return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Use the theme's locale/proxy-aware routes + fetch config, not hardcoded /cart/*.js
  // (those resolve to an HTML page on dev/localized stores → JSON parse errors).
  function cartJson() {
    return fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET'), cache: 'no-store' }).then((r) => r.json());
  }

  function cartChange(key, qty) {
    return fetch(theme.routes.cart_change_url, {
      ...theme.utils.fetchConfig('json'),
      body: JSON.stringify({ id: key, quantity: qty })
    }).then((r) => r.json());
  }

  // Section ids the theme bundles into a cart mutation (cart drawer pushes its own).
  function bundledSectionIds() {
    const ids = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: ids } })
    );
    return ids;
  }

  // Re-render the drawer once via the theme's cartUpdate path; fall back to
  // cart:refresh if no section HTML came back (onCartUpdate needs cart.sections).
  function publishCartUpdate(cart, sections) {
    if (sections && Object.keys(sections).length) {
      cart.sections = sections;
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'ck-gift', cart });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    }
  }

  // Any of our free-gift lines — picker (_ck_gift) or preselected auto (_ck_gift_auto).
  function isGift(item) {
    return item.properties && (item.properties._ck_gift === '1' || item.properties._ck_gift_auto === '1');
  }

  // Matched on the _ck_gift tag alone, never the variant: the merchant can swap a
  // picker's products while a gift sits in someone's cart, and a variant-scoped match
  // would stop recognising that line — leaving it free and granting a second one.
  function findPickerGifts(cartItems) {
    return cartItems.filter((i) => i.properties && i.properties._ck_gift === '1');
  }

  // A preselected auto-gift line (_ck_gift_auto) for a given variant.
  function findAutoLine(cartItems, variantId) {
    return cartItems.find((i) => i.properties && i.properties._ck_gift_auto === '1' && i.variant_id === variantId);
  }

  function pickerStyleVars(picker) {
    const map = {
      '--ck-gt-picker-bg': picker.bg_color,
      '--ck-gt-picker-heading': picker.heading_color,
      '--ck-gt-picker-card-bg': picker.card_bg_color,
      '--ck-gt-picker-card-border': picker.card_border_color,
      '--ck-gt-picker-card-text': picker.card_text_color,
      '--ck-gt-picker-btn-bg': picker.button_bg_color,
      '--ck-gt-picker-btn-text': picker.button_text_color,
      '--ck-gt-picker-badge-text': picker.badge_text_color
    };
    const parts = [];
    for (const k in map) { if (map[k]) parts.push(k + ':' + map[k]); }
    if (picker.card_border_radius != null) parts.push('--ck-gt-picker-radius:' + picker.card_border_radius + 'px');
    return parts.join(';');
  }

  var CHEVRON_L = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M10 3 5 8l5 5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_R = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function renderPickers(ckTotals, cartItems, config) {
    const host = getPickerHost();
    if (!host) return;
    if (!config.pickers || !config.pickers.length) { host.innerHTML = ''; return; }

    // One gift per cart.
    const giftHeld = findPickerGifts(cartItems).length > 0;

    let html = '';
    config.pickers.forEach((picker, pi) => {
      if (ckStepValue(picker, ckTotals) < picker.threshold_cents) return; // locked
      if (giftHeld) return; // gift already taken
      const options = (picker.products || []).filter((p) => p.available && p.variant_id);
      if (!options.length) return;
      const vars = pickerStyleVars(picker);
      html += '<div class="ck-gt-picker" data-ck-gt-picker="' + pi + '"' + (vars ? ' style="' + vars + '"' : '') + '>';

      html += '<div class="ck-gt-picker__head">';
      if (picker.heading) html += '<p class="ck-gt-picker__heading">' + picker.heading + '</p>';
      if (picker.show_arrows !== false) {
        html += '<div class="ck-gt-picker__nav">' +
          '<button type="button" class="ck-gt-picker__arrow" data-ck-gt-picker-prev aria-label="Previous">' + CHEVRON_L + '</button>' +
          '<button type="button" class="ck-gt-picker__arrow" data-ck-gt-picker-next aria-label="Next">' + CHEVRON_R + '</button>' +
          '</div>';
      }
      html += '</div>';

      html += '<div class="ck-gt-picker__track" data-ck-gt-picker-track>';
      options.forEach((p) => {
        html += '<div class="ck-gt-picker__card">' +
          '<a class="ck-gt-picker__media" href="' + escHtml(p.url) + '" tabindex="-1">' +
          (p.image ? '<img src="' + escHtml(p.image) + '" alt="" loading="lazy">' : '') + '</a>' +
          '<p class="ck-gt-picker__title">' + escHtml(p.title) + '</p>' +
          (p.subtitle ? '<p class="ck-gt-picker__subtitle">' + escHtml(p.subtitle) + '</p>' : '') +
          '<div class="ck-gt-picker__prices">' +
          (p.price ? '<s class="ck-gt-picker__price">' + formatMoney(p.price, config.money_format) + '</s>' : '') +
          (picker.badge_text ? '<span class="ck-gt-picker__badge">' + escHtml(picker.badge_text) + '</span>' : '') +
          '</div>' +
          '<button is="hover-button" type="button" class="button button--primary icon-with-text col-span-full ck-gt-picker__add" data-ck-gt-gift-add data-variant="' + p.variant_id + '" data-picker="' + pi + '">' +
          '<span class="btn-fill" data-fill></span>' +
          '<span class="btn-text">' + escHtml(picker.button_text || 'Hinzufügen') + '</span>' +
          '</button>' +
          '</div>';
      });
      html += '</div>';

      if (picker.show_dots !== false) html += '<div class="ck-gt-picker__dots" data-ck-gt-picker-dots></div>';
      html += '</div>';
    });
    host.innerHTML = html;

    Array.prototype.forEach.call(host.querySelectorAll('.ck-gt-picker'), setupPickerCarousel);
  }

  function setupPickerCarousel(el) {
    const track = el.querySelector('[data-ck-gt-picker-track]');
    if (!track) return;
    const prev = el.querySelector('[data-ck-gt-picker-prev]');
    const next = el.querySelector('[data-ck-gt-picker-next]');
    const dots = el.querySelector('[data-ck-gt-picker-dots]');

    function cardStep() {
      const card = track.querySelector('.ck-gt-picker__card');
      if (!card) return track.clientWidth;
      const cs = getComputedStyle(track);
      const gap = parseFloat(cs.columnGap || cs.gap || 0) || 0;
      return card.getBoundingClientRect().width + gap;
    }
    function update() {
      const overflow = track.scrollWidth - track.clientWidth > 1;
      const x = track.scrollLeft;
      const max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = !overflow || x <= 1;
      if (next) next.disabled = !overflow || x >= max - 1;
      if (dots && dots.children.length) {
        const active = Math.round(x / (cardStep() || 1));
        Array.prototype.forEach.call(dots.children, (d, i) => d.classList.toggle('is-active', i === active));
      }
    }
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -cardStep(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: cardStep(), behavior: 'smooth' }));
    track.addEventListener('scroll', update, { passive: true });

    // Pointer drag-to-scroll (same behaviour as the cart upsell).
    (function enableDrag() {
      let down = false, dragging = false, startX = 0, startScroll = 0, pid = null;
      track.addEventListener('pointerdown', (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        down = true; dragging = false; startX = e.clientX; startScroll = track.scrollLeft; pid = e.pointerId;
      });
      track.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!dragging) {
          if (Math.abs(dx) <= 5) return; // not a drag yet — let clicks through
          dragging = true;
          track.classList.add('is-dragging');
          try { track.setPointerCapture(pid); } catch (err) {}
        }
        track.scrollLeft = startScroll - dx;
      });
      const end = () => {
        if (!down) return;
        down = false;
        if (dragging) {
          track.classList.remove('is-dragging');
          try { track.releasePointerCapture(pid); } catch (err) {}
        }
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
      // Swallow only the click that follows a real drag, so a drag doesn't add a gift.
      track.addEventListener('click', (e) => {
        if (dragging) { e.preventDefault(); e.stopPropagation(); dragging = false; }
      }, true);
    })();

    if (dots) {
      const count = track.querySelectorAll('.ck-gt-picker__card').length;
      dots.innerHTML = '';
      dots.hidden = count <= 1;
      for (let i = 0; i < count; i++) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'ck-gt-picker__dot';
        b.setAttribute('aria-label', String(i + 1));
        b.addEventListener('click', () => track.scrollTo({ left: i * cardStep(), behavior: 'smooth' }));
        dots.appendChild(b);
      }
    }
    update();
    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(track);
    }
  }

  // Guards gift add/remove so cart re-renders don't re-enter. A watchdog clears
  // the lock if a request ever stalls, so the picker can't freeze permanently.
  function setPickerBusy(on) {
    _pickerBusy = on;
    clearTimeout(_busyTimer);
    if (on) _busyTimer = setTimeout(() => { _pickerBusy = false; }, 6000);
  }

  async function addGift(variantId) {
    if (_pickerBusy) return;
    setPickerBusy(true);
    try {
      // Clear every picker gift first, including one no longer offered by the picker.
      const cart0 = await cartJson();
      for (const gift of findPickerGifts(cart0.items)) {
        await cartChange(gift.key, 0);
      }

      const added = await fetch(theme.routes.cart_add_url, {
        ...theme.utils.fetchConfig('json'),
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { _ck_gift: '1' }, sections: bundledSectionIds() })
      }).then((r) => r.json());
      const cart = await cartJson();
      publishCartUpdate(cart, added.sections);
    } catch (e) {
      console.error('[ck-gt-pb] addGift', e);
    } finally {
      setPickerBusy(false);
      fetchAndUpdate();
    }
  }

  // Keep gifts consistent on every cart read (init + cart change). One action per pass;
  // fetchAndUpdate re-runs until nothing's left to do. The Function prices the gift line;
  // the theme only adds it when earned and removes it when not.
  //  Preselected auto gifts: add when earned, remove when not.
  //  Picker gifts:           remove when below threshold.
  async function reconcilePickers(ckTotals, cart, config) {
    if (_pickerBusy) return;
    const items = cart.items;
    let action = null;

    // Preselected auto gifts (product blocks)
    for (const g of (config.auto_gifts || [])) {
      if (!g.variant_id) continue;
      const line = findAutoLine(items, g.variant_id);
      const earned = ckStepValue(g, ckTotals) >= g.threshold_cents;
      // Pinned variant, so a sold-out gift would fail /cart/add on every update.
      if (earned && !line && g.available) { action = { type: 'add', variantId: g.variant_id }; break; }
      if (!earned && line) { action = { type: 'remove', key: line.key }; break; }
    }

    // Picker gifts. Matched by tag, so an orphan is cleaned up rather than staying free.
    if (!action) {
      const gifts = findPickerGifts(items);
      if (gifts.length) {
        const pickers = config.pickers || [];
        // Assumes "limit": 1 on the gift_picker block. Raising it means matching each
        // gift back to the picker that granted it.
        const earned = pickers.some((p) => ckStepValue(p, ckTotals) >= p.threshold_cents);
        if (gifts.length > 1) {
          // Carts from before this fix. Drop the oldest, keep their latest choice.
          action = { type: 'remove', key: gifts[0].key };
        } else if (!earned) {
          action = { type: 'remove', key: gifts[0].key };
        }
      }
    }

    if (!action) return;
    setPickerBusy(true);
    let mutated = false;
    try {
      if (action.type === 'remove') {
        // /cart/change returns the full cart + bundled section HTML.
        const cart = await fetch(theme.routes.cart_change_url, {
          ...theme.utils.fetchConfig('json'),
          body: JSON.stringify({ id: action.key, quantity: 0, sections: bundledSectionIds() })
        }).then((r) => r.json());
        publishCartUpdate(cart, cart.sections);
        mutated = true;
      } else { // add
        // /cart/add returns the added line + section HTML; read full cart for totals.
        const added = await fetch(theme.routes.cart_add_url, {
          ...theme.utils.fetchConfig('json'),
          body: JSON.stringify({ id: action.variantId, quantity: 1, properties: { _ck_gift_auto: '1' }, sections: bundledSectionIds() })
        }).then((r) => r.json());
        const cart = await cartJson();
        publishCartUpdate(cart, added.sections);
        mutated = true;
      }
    } catch (e) {
      console.error('[ck-gt-pb] reconcilePickers', e);
    } finally {
      setPickerBusy(false);
      if (mutated) fetchAndUpdate();
    }
  }

  // ─── Qualifying totals ────────────────────────────────────────────────────
  // Gift lines never count, or a gift would unlock the next tier by itself.

  function ckCartTotals(cart, config, cartCollections) {
    const ckHandles = [...new Set((config.thresholds || []).map((t) => t.ck_collection).filter(Boolean))];
    let ckGiftCents = 0;
    let ckSubtotal = 0;
    const ckByCollection = {};
    ckHandles.forEach((handle) => { ckByCollection[handle] = cartCollections ? 0 : null; });

    cart.items.forEach((item) => {
      if (isGift(item)) { ckGiftCents += item.final_line_price; return; }
      ckSubtotal += item.original_line_price;
      if (!cartCollections) return;
      const ckItemHandles = cartCollections[item.product_id] || [];
      ckHandles.forEach((handle) => {
        if (ckItemHandles.includes(handle)) ckByCollection[handle] += item.original_line_price;
      });
    });

    // cart.total_price, not the line prices: only it includes order-level codes (DOC20).
    return { total: Math.max(cart.total_price - ckGiftCents, 0), subtotal: ckSubtotal, byCollection: ckByCollection };
  }

  function ckStepValue(step, ckTotals) {
    if (step.ck_basis !== 'subtotal') return ckTotals.total;
    if (!step.ck_collection) return ckTotals.subtotal;
    const ckValue = ckTotals.byCollection[step.ck_collection];
    // Collection couldn't be loaded: the whole subtotal is the closest guess.
    return ckValue === null || ckValue === undefined ? ckTotals.subtotal : ckValue;
  }

  // { product_id: [collection handles] } for the products in the cart, from
  // sections/ck-cart-collections.liquid. Only those products, because a whole discount
  // collection via products.json is several MB.
  function ckFetchCartCollections(config) {
    if (!(config.thresholds || []).some((t) => t.ck_collection)) return Promise.resolve(null);
    return fetch(`${theme.routes.cart_url}?section_id=ck-cart-collections`, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then((html) => {
        const ckEl = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ck-cart-collections]');
        return JSON.parse(ckEl.textContent);
      })
      .catch((e) => {
        console.error('[ck-gt-pb] cart collections', e);
        return null;
      });
  }

  // ─── Fetch cart and repaint the bar ───────────────────────────────────────

  async function fetchAndUpdate() {
    const config = getConfig();
    if (!config || !config.enabled) return;

    try {
      // Always read fresh — the cart:updated snapshot may omit line-item properties,
      // which we need to identify tagged gifts.
      const [cart, cartCollections] = await Promise.all([cartJson(), ckFetchCartCollections(config)]);
      const items = cart.items;
      const ckTotals = ckCartTotals(cart, config, cartCollections);
      _lastCart = { ckTotals: ckTotals, items: items };
      try { updateUI(ckTotals, items, config); } catch (e) { console.error('[ck-gt-pb] updateUI', e); }
      reconcilePickers(ckTotals, cart, config);
    } catch (e) {
      console.error('[ck-gt-pb] Fetch error', e);
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function init() {
    const config = getConfig();
    if (!config || !config.enabled) return;

    injectBar();
    fetchAndUpdate();
    observeRerenders();

    // Gift "Add" buttons (delegated — the picker markup is re-rendered often).
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-ck-gt-gift-add]');
      if (!btn) return;
      e.preventDefault();
      const cfg = getConfig();
      const variantId = Number(btn.getAttribute('data-variant'));
      const picker = cfg && cfg.pickers && cfg.pickers[Number(btn.getAttribute('data-picker'))];
      if (variantId && picker) addGift(variantId);
    });

    // Repaint the bar whenever the drawer re-renders (fires cart:updated) — incl.
    // our own gift add/remove, which now routes through that same cartUpdate path.
    document.addEventListener('cart:updated', (e) => {
      var c = e && e.detail && e.detail.cart;

      // Ignore duplicate events whose cart contents are unchanged (avoids
      // redundant repaints when the cart re-renders without actually changing).
      if (c) {
        var sig = cartSig(c);
        if (sig === _cartSig) {
          injectBar(); // still re-render the bar after a passive re-render
          return;
        }
        _cartSig = sig;
      }

      injectBar();
      clearTimeout(_fetchTimer);
      _fetchTimer = setTimeout(() => fetchAndUpdate(), 20);
    });

    // Re-init if merchant changes settings in the customizer
    document.addEventListener('shopify:section:load', () => {
      _config = null;
      injectBar();
      fetchAndUpdate();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
